/**
 * Inactivity Warning Modal - displays when user is about to be logged out due to inactivity.
 */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import { useInactivity } from "../../contexts/InactivityContext";
import { useAuth } from "../../contexts/AuthContext";

export const InactivityWarningModal: React.FC = () => {
  const { isWarningVisible, remainingSeconds, dismissWarning } = useInactivity();
  const { signOut } = useAuth();

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleStayLoggedIn = () => {
    dismissWarning();
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // Ignore sign out errors, will redirect anyway
    }
  };

  return (
    <Dialog
      open={isWarningVisible}
      onClose={handleStayLoggedIn}
      maxWidth="xs"
      fullWidth
      aria-labelledby="inactivity-dialog-title"
      aria-describedby="inactivity-dialog-description"
    >
      <DialogTitle id="inactivity-dialog-title">Session Timeout Warning</DialogTitle>
      <DialogContent>
        <Typography id="inactivity-dialog-description" variant="body1" sx={{ mb: 2 }}>
          Your session will expire due to inactivity in{" "}
          <strong>{formatTime(remainingSeconds)}</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Click &quot;Stay Logged In&quot; to continue your session, or sign out now.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSignOut}>Sign Out</Button>
        <Button onClick={handleStayLoggedIn} variant="contained">
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};
